import { CertGenParams } from '@yandex-int/k-common';
import {
    Button,
    CloseButtonViewOuter,
    InputChangeEvent,
    Modal,
    ModalCloseProps,
    H3,
    SimpleCallback,
    SimpleText,
    Text,
    useIntl,
    useMe,
} from 'platform-components';
import { UI_KIT_HEADER_TYPOGRAPHY, UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/constants';
import { useMetrika } from 'platform-components/src/utils/hooks/useMetrika';
import { isValidHumanName } from 'platform-components/utils';
import React, { useState, useEffect } from 'react';
import { useMount } from 'react-use';

import { splitFullName } from 'common.components/certificate-modal/utils';

import CertificatesParamsModalFormField, {
    CERTIFICATES_PARAMS_MODAL_DATA_TYPE,
} from './__form-field/certificates-params-modal__form-field';

import './certificates-params-modal.scss';

export interface CertificatesParamsModalData {
    fullName: string;
    locality: string;
    school: string;
}

interface Props {
    certificatesData: CertificatesParamsModalData;
    closeModal: SimpleCallback;
    onApprove: (params: CertGenParams) => void;
    progress: boolean;
}

const CertificatesParamsModal = (props: Props) => {
    const { certificatesData, closeModal, onApprove, progress } = props;
    const intl = useIntl();
    const me = useMe();
    const { sendGoal } = useMetrika();

    const [formData, setFormData] = useState(certificatesData);
    const [errorMessages, setErrorMessages] = useState({ fullName: null, school: null, locality: null });

    useMount(() => {
        sendGoal('teacher_navigation_lk_start_form', { teacher_id: me?.id });
    });

    useEffect(() => {
        setFormData(certificatesData);
    }, [certificatesData]);

    const handleChangeValue = ({ target: { value } }: InputChangeEvent, type: CERTIFICATES_PARAMS_MODAL_DATA_TYPE) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            [type]: value.replace(/\s+/g, ' '),
        }));
        setErrorMessages((prevErrorMessages) => ({
            ...prevErrorMessages,
            [type]: null,
        }));
    };

    const handleValidate = (type: CERTIFICATES_PARAMS_MODAL_DATA_TYPE) => {
        let errorMessageId: string | null = null;

        if (!formData[type]) {
            errorMessageId = `certificatesParamsModal.${type}.required`;
        } else if (!isValidHumanName(formData.fullName)) {
            errorMessageId = 'certificatesParamsModal.fullName.invalid';
        }

        const errorMessageStr = errorMessageId ? intl.formatMessage({ id: errorMessageId }) : null;

        setErrorMessages((prevErrorMessages) => ({
            ...prevErrorMessages,
            [type]: errorMessageStr,
        }));

        return !errorMessages[type];
    };

    const getIsFormValid = () => {
        let isValid = true;
        let key: keyof CertificatesParamsModalData;

        for (key in formData) {
            if (!formData[key] || errorMessages[key]) {
                isValid = false;
            }
        }

        return isValid;
    };

    const handleApprove = () => {
        const { fullName, school, locality } = formData;
        const { first_name = '', last_name = '', middle_name = '' } = splitFullName(fullName);
        const now = new Date().toISOString();

        sendGoal('teacher_navigation_lk_finish_form', { teacher_id: me?.id });
        onApprove({ first_name, last_name, middle_name, school_name: school, locality, date_updated: now });
        closeModal();
    };

    const handleClose = () => {
        sendGoal('teacher_navigation_lk_left_form', { teacher_id: me?.id });

        closeModal();
    };

    return (
        <Modal
            Close={(closeProps: ModalCloseProps) => (
                <CloseButtonViewOuter {...closeProps} position={CloseButtonViewOuter.POSITION.ABSOLUTE} />
            )}
            hasClose
            mix="certificates-params-modal"
            onRequestClose={handleClose}
            opened={true}
            theme={Modal.THEMES.TEACHER}
        >
            <Modal.Header>
                <H3 id="certificatesParamsModal.title" typography={UI_KIT_HEADER_TYPOGRAPHY.headlineMMedium} />
            </Modal.Header>
            <Modal.Content>
                <Text
                    className="certificates-params-modal__description"
                    id="certificatesParamsModal.description"
                    type={Text.TYPE.MARKDOWN}
                    typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLRegular}
                />
                {Object.values(CERTIFICATES_PARAMS_MODAL_DATA_TYPE).map((type: CERTIFICATES_PARAMS_MODAL_DATA_TYPE) => (
                    <CertificatesParamsModalFormField
                        errorMessage={errorMessages[type]}
                        onBlur={() => handleValidate(type)}
                        onChangeValue={(e) => handleChangeValue(e, type)}
                        type={type}
                        value={formData[type]}
                    />
                ))}
            </Modal.Content>
            <Modal.Footer>
                <Button onClick={handleClose} view={Button.VIEW.SECONDARY}>
                    <SimpleText id="certificatesParamsModal.cancelButtonText" />
                </Button>
                <Button
                    disabled={!getIsFormValid()}
                    onClick={handleApprove}
                    progress={progress}
                    view={Button.VIEW.ACTION}
                >
                    <SimpleText id="certificatesParamsModal.approveButtonText" />
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CertificatesParamsModal;
